// /common/spBase.js
// どの案件でも使える共通初期化：ヘッダー高→CSS変数、アンカー補正、（あれば）ドロワー開閉、A11y
export function initSpBase() {
  const root = document.documentElement;
  const app = document.querySelector(".js-app") || document.getElementById("app") || document.body;
  const header = document.querySelector(".js-header") || document.querySelector(".g-header");
  const mv = document.querySelector(".js-mv") || document.querySelector(".l-mv") || document.querySelector(".mv");

  // 早期終了：app/headerだけは必須（以降は存在チェックで分岐）
  if (!app || !header) return;

  // JSアンカー制御フラグ（CSS側の保険にも使える）
  root.setAttribute("data-js-anchor", "on");

  // ========= ヘッダー高さ → CSS変数 / アンカーずれ対策 =========
  const setHeaderHeight = () => {
    const h = header.offsetHeight || 56;
    root.style.setProperty("--header-h", `${h}px`);
    // 代表セクションにだけ明示しておく（全体はCSS保険で対応）
    if (mv) mv.style.scrollMarginTop = `${h}px`;
  };
  setHeaderHeight();
  addEventListener("resize", setHeaderHeight, { passive: true });
  addEventListener("orientationchange", setHeaderHeight);
  addEventListener("pageshow", setHeaderHeight);
  header.addEventListener("transitionend", (e) => {
    if (["height", "padding", "transform"].includes(e.propertyName)) setHeaderHeight();
  });

  // ========= KVより下で header.active =========
  const toggleHeaderByPosition = () => {
    const kvBottom = mv ? mv.offsetHeight : 0;
    header.classList.toggle("active", window.scrollY > kvBottom);
  };
  addEventListener("scroll", toggleHeaderByPosition, { passive: true });
  toggleHeaderByPosition();

  // ========= ドロワー（ハンバーガーがあれば動く） =========
  const burger = document.querySelector(".js-burger");
  const nav = document.querySelector(".js-nav");
  const viewport = document.querySelector(".js-nav-viewport");

  let lockY = 0;
  const isOpen = () => root.hasAttribute("data-nav-open");

  function lockScroll() {
    lockY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${lockY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  }
  function unlockScroll() {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    window.scrollTo(0, lockY);
    requestAnimationFrame(() => (html.style.scrollBehavior = prev));
  }

  const raf2 = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

  if (burger && nav) {
    // A11y調整
    burger.setAttribute("aria-expanded", "false");
    nav.hidden = true;
    viewport?.setAttribute("aria-hidden", "true");

    function openMenu() {
      nav.hidden = false;
      raf2(() => {
        viewport?.removeAttribute("aria-hidden");
        root.setAttribute("data-nav-open", "true");
        app.classList.add("is-locked");
        burger.classList.add("is-active");
        burger.setAttribute("aria-expanded", "true");
        lockScroll();
        // メニュー内の先頭フォーカスへ
        const first = nav.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
        first && first.focus?.({ preventScroll: true });
      });
    }
    function closeMenu(afterClose) {
      root.removeAttribute("data-nav-open");
      app.classList.remove("is-locked");
      burger.classList.remove("is-active");
      burger.setAttribute("aria-expanded", "false");

      let done = false;
      const onEnd = (e) => {
        if (done) return;
        // transformで閉じる想定。違う場合でもフォールバックで閉じます
        if (e && (e.target !== nav || e.propertyName !== "transform")) return;
        done = true;
        nav.hidden = true;
        unlockScroll();
        viewport?.setAttribute("aria-hidden", "true");
        burger.focus?.({ preventScroll: true });
        typeof afterClose === "function" && requestAnimationFrame(afterClose);
      };
      nav.addEventListener("transitionend", onEnd, { once: true });
      setTimeout(onEnd, 420); // フォールバック
    }
    function toggleMenu() {
      isOpen() ? closeMenu() : openMenu();
    }

    burger.addEventListener("click", toggleMenu);
    addEventListener("keydown", (e) => {
      if (e.key === "Escape" && isOpen()) closeMenu();
    });
    if (viewport) {
      viewport.addEventListener("click", (e) => {
        if (e.target === viewport && isOpen()) closeMenu();
      });
    }

    // 外部でも使えるように一応公開
    Object.assign(nav, { openMenu, closeMenu });
  }

  // ========= アンカーリンク（全ページ共通で補正） =========
  const getHeaderH = () => {
    const v = getComputedStyle(root).getPropertyValue("--header-h");
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 56;
  };
  function scrollToTarget(target, { smooth = true } = {}) {
    const y = target.getBoundingClientRect().top + window.scrollY - getHeaderH();
    window.scrollTo({ top: Math.max(0, y), behavior: smooth ? "smooth" : "auto" });
  }

  document.addEventListener("click", (e) => {
    const a = e.target.closest?.("a[href]");
    if (!a) return;

    const href = a.getAttribute("href");
    if (!href || href.startsWith("javascript:")) return;

    const url = new URL(href, location.href);
    const isSameDoc = url.origin === location.origin && url.pathname === location.pathname && url.hash.length > 1;
    if (!isSameDoc) return;

    const id = decodeURIComponent(url.hash.slice(1));
    const target = document.getElementById(id);
    if (!target) return;

    e.preventDefault();
    const after = () => {
      scrollToTarget(target, { smooth: true });
      // ハッシュはURLから除去（履歴を汚さない）
      history.replaceState?.(null, "", `${location.pathname}${location.search}`);
      // A11y: フォーカス同期
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
      setTimeout(() => target.removeAttribute("tabindex"), 0);
    };

    // ドロワー内のリンクは閉じてからスクロール（存在する場合のみ）
    if (isOpen() && nav?.contains(a)) nav.closeMenu?.(after);
    else after();
  });

  // ========= 初回 #hash 調整 =========
  const hadHashAtStart = location.hash.length > 1;
  let prevScrollRestoration = null;
  if ("scrollRestoration" in history && hadHashAtStart) {
    prevScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }
  addEventListener("load", () => {
    if (location.hash.length > 1) {
      const id = decodeURIComponent(location.hash.slice(1));
      const target = document.getElementById(id);
      if (target) {
        // ①即時
        scrollToTarget(target, { smooth: false });
        // ②描画安定後
        requestAnimationFrame(() => requestAnimationFrame(() => scrollToTarget(target, { smooth: false })));
        // ③遅延リソース後
        setTimeout(() => scrollToTarget(target, { smooth: false }), 250);
        // URLからハッシュ除去 & フォーカス同期
        history.replaceState?.(null, "", `${location.pathname}${location.search}`);
        target.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: true });
        setTimeout(() => target.removeAttribute("tabindex"), 0);
      }
    }
    if ("scrollRestoration" in history && prevScrollRestoration != null) {
      history.scrollRestoration = prevScrollRestoration || "auto";
    }
  });
}
